/* eslint-disable react/prop-types */
import clsx from 'clsx';
import type { MouseEvent, ReactNode } from 'react';
import { useState } from 'react';
import type { JsonData, JsonDataItem } from '../generic-types';
import type { TemplateType } from '../template-outlet/template-outlet.component';
import TemplateOutlet from '../template-outlet/template-outlet.component';
import type { DataGridCellRenderer } from './data-grid-custom-column.interface';
import DataGridDataRowItem from './data-grid-data-row-item.component';
import { findRowIndex, getColumnDefaultWidth } from './data-grid-functions';
import type { DataGridColumnSettings, DataGridRowIndicator } from './data-grid-settings.interface';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import { useSignalEffect } from '@preact/signals-react';
import useOdinMemo from '../../common/hooks/use-odin-memo';
import useTestTag from '../../common/hooks/use-test-tag';
import { SignalForFilter } from '../signals/for-filter/signal-for-filter.component';
import type { OdinKey } from '../../common/types/odin-key.type';

export interface DataGridDataRowProps {
  columns: Signal<Array<DataGridColumnSettings>>;
  selectedRows: Signal<JsonData>;
  highlightedRows: Signal<JsonData>;
  idField: ReadonlySignal<DataGridColumnSettings | undefined>;
  rowIndex: number;
  data: JsonDataItem;
  isEven: boolean;
  rowIndicator?: DataGridRowIndicator;
  rowIsClickable?: boolean;
  components?: Record<string, DataGridCellRenderer>;
  rowTemplate?: RowTemplateType;
  allowCellScrollBar?: boolean;
  autoSizeColumns?: boolean;
}

export interface DataGridDataRowTemplateProps extends DataGridDataRowProps {
  isRowSelected: boolean;
  onRowClicked: (event: MouseEvent<HTMLElement>) => void;
}

export type RowTemplateType = TemplateType<DataGridDataRowTemplateProps>;

export default function DataGridDataRow({
  columns,
  selectedRows,
  highlightedRows,
  idField,
  rowIndex,
  data,
  isEven,
  rowIndicator,
  rowIsClickable,
  components,
  rowTemplate,
  allowCellScrollBar,
  autoSizeColumns,
}: DataGridDataRowProps) {
  const tagCreator = useTestTag('row');

  const [isRowSelected, setIsRowSelected] = useState(false);
  const [isRowHighlighted, setIsRowHighlighted] = useState(false);

  useSignalEffect(() => {
    setIsRowSelected(findRowIndex(selectedRows.value, data, idField.value) > -1);
  });

  useSignalEffect(() => {
    setIsRowHighlighted(findRowIndex(highlightedRows.value, data, idField.value) > -1);
  });

  const onRowClicked = () => {
    selectedRows.value = [data];
  };

  const onIndicatorClicked = () => {
    onRowClicked();

    if (idField.value && rowIndicator?.onIndicatorClicked) {
      rowIndicator.onIndicatorClicked(data[idField.value.field] as OdinKey);
    }
  };

  const templateProps: DataGridDataRowTemplateProps = {
    columns,
    selectedRows,
    highlightedRows,
    idField,
    rowIndex,
    data,
    isEven,
    rowIsClickable,
    components,
    isRowSelected,
    onRowClicked,
  };

  const defaultTemplate = (props: DataGridDataRowTemplateProps, children?: ReactNode | Array<ReactNode>) => {
    const onRowClick = (event: MouseEvent<HTMLElement>) => {
      if (props.rowIsClickable) {
        props.onRowClicked(event);
      }
    };

    return (
      <div className="odin-flex" onClick={onRowClick}>
        {children}
      </div>
    );
  };

  const template = rowTemplate || defaultTemplate;

  const rowContainerClassNames = clsx('odin-flex odin-group odin-min-w-full odin-text-sm', {
    'odin-cursor-default': !rowIsClickable,
  });

  const rowClassNames = clsx('odin-flex', {
    'odin-bg-gray-5': !isEven && !isRowSelected,
    'odin-bg-mono-1': isEven && !isRowSelected,
    'odin-bg-primary-4': isRowHighlighted && !isRowSelected,
    'odin-bg-primary-3 odin-text-mono-1 hover:odin-text-mono-0': isRowSelected,
    'odin-cursor-pointer hover:odin-bg-primary-4': rowIsClickable,
  });

  // NOTE: if modifying the width 'odin-w-10', please remember to update 'STATUS_INDICATOR_CELL_WIDTH_PX'
  const indicatorClassNames = clsx(
    'odin-bg-mono-1 odin-flex odin-items-center odin-justify-center odin-self-stretch odin-w-10 odin-py-2 odin-flex-shrink-0',
    rowIndicator?.indicatorClassNames
  );

  const indicatorInnerClassNames = clsx({
    'odin-opacity-0 odin-transition-opacity group-hover:odin-opacity-100 odin-cursor-pointer':
      rowIndicator?.showOnHoverOnly === true,
    'odin-opacity-100': isRowSelected,
  });

  const rowData = useOdinMemo<JsonDataItem>(() => {
    return {
      ...data,
      rowIndex,
    };
  }, [data, rowIndex]);

  return (
    <div className={rowContainerClassNames}>
      {rowIndicator && (
        <div className={indicatorClassNames} onClick={onIndicatorClicked}>
          <div className={indicatorInnerClassNames}>{rowIndicator.indicator(data)}</div>
        </div>
      )}
      <TemplateOutlet props={templateProps} template={template}>
        <div className={rowClassNames} ref={tagCreator(String(rowIndex))}>
          <SignalForFilter items={columns} filter={(column) => column.visible}>
            {(column) => {
              if (autoSizeColumns !== true) {
                column.width = column.width || getColumnDefaultWidth(column);
              }

              return (
                <DataGridDataRowItem
                  key={`${rowIndex}_${column.id}`}
                  item={rowData}
                  column={column}
                  components={components}
                  allowCellScrollBar={allowCellScrollBar}
                />
              );
            }}
          </SignalForFilter>
        </div>
      </TemplateOutlet>
    </div>
  );
}
